// FAZ 2B — SÜRÜKLE-BIRAK ile "İÇİNE TAŞIMA" hedef noktası ("İÇİNE").
//
// NEDEN BÖYLE: Hedef tespiti dnd-kit'in KENDİ droppable sistemiyle yapılır
// (geometri/elementsFromPoint tahmini YOK). Uygulamada iki ayrı dnd-kit sürümü
// kullanılıyor:
//   • @dnd-kit/react (Detaylı/Profesyonel/Teknik kart listeleri, alt görevler)
//   • @dnd-kit/core   (Kolay arayüzü)
// Her ikisi için de nokta AYNI görünümde, ayrı droppable kancasıyla bağlanır.
// Sıralama (sortable) mantığına dokunulmaz: nokta yalnızca imleç TAM üstündeyken
// (pointer kesişimi) ve EN YÜKSEK öncelikle hedef olur; nokta hedefken sıralama
// önizlemesi uygulanmaz.
import { createContext, useContext, useEffect, useMemo, useRef, useState, startTransition } from "react";
import { CornerDownRight } from "lucide-react";
import { useDroppable as useReactDroppable } from "@dnd-kit/react";
import { CollisionPriority } from "@dnd-kit/abstract";
import { pointerIntersection } from "@dnd-kit/collision";
import { useDroppable as useCoreDroppable, pointerWithin, closestCenter } from "@dnd-kit/core";
import { AutoScroller } from "@dnd-kit/dom";
import { crossAbone, crossBaslat, crossBitir, crossHedef, useCrossNest } from "./crossWindowDrag";
import { SURUKLE_HIZLI_MOD_ACIK } from "./surukleHizli";

export const SURUKLE_ICINE_TASI_ACIK = true;
export const NEST_KAPALI = { aktif: false, gecersiz: null };

// Nokta YALNIZCA sürükleme sırasında ve YALNIZCA geçerli hedeflerde render
// edilir. Geçersizde hiç çizilmediği için "bırakılamaz" durumu yaşanmaz.
export const NestDropContext = createContext(NEST_KAPALI);

// FAZ 4 (hızlı mod) — nokta durumu React state'i yerine küçük bir depoda
// tutulur: sürükleme başlayınca liste/kartlar DEĞİL yalnızca noktalar çizilir.
// Depo kapsam başına birdir (sidebar listesi, her pencere) — eski
// `nest.aktif ? nest : dis` kuralı birebir korunur.
export const NestDepoContext = createContext(null);

const nestDeposuOlustur = () => {
  let yerel = NEST_KAPALI;
  let dis = NEST_KAPALI;
  const dinleyiciler = new Set();
  const durum = () => (yerel.aktif ? yerel : dis);
  const yay = () => dinleyiciler.forEach((f) => f());
  return {
    yerelAktif: () => yerel.aktif,
    yerelAyarla: (v) => { yerel = v; yay(); },
    disAyarla: (v) => { dis = v; yay(); },
    abone: (f) => { dinleyiciler.add(f); return () => dinleyiciler.delete(f); },
    gorunur: (anahtar) => {
      const d = durum();
      return SURUKLE_ICINE_TASI_ACIK && d.aktif && !d.gecersiz?.has?.(anahtar);
    },
  };
};

export const useNestDeposu = (kapsam) => {
  const [depo] = useState(() => (SURUKLE_HIZLI_MOD_ACIK ? nestDeposuOlustur() : null));
  useEffect(() => {
    if (!depo) return undefined;
    return crossAbone((d) => depo.disAyarla(
      d.aktif && d.kaynakKapsam !== kapsam ? { aktif: true, gecersiz: d.gecersiz } : NEST_KAPALI,
    ));
  }, [depo, kapsam]);
  return depo;
};

export const NEST_PRE = "nest::";
export const nestDropId = (taskId, subId = null) => `${NEST_PRE}${taskId}::${subId || ""}`;
export const parseNestId = (id) => {
  if (typeof id !== "string" || !id.startsWith(NEST_PRE)) return null;
  const [taskId, subId] = id.slice(NEST_PRE.length).split("::");
  return taskId ? { taskId, subId: subId || null } : null;
};

// @dnd-kit/core (Kolay arayüzü) için çarpışma tespiti: "İÇİNE" noktaları imleç
// kesişimiyle ÖNCE denenir; yoksa normal sıralama davranışı (closestCenter).
export const nestCollisionDetection = (args) => {
  const kaplar = args.droppableContainers || [];
  const nestler = kaplar.filter((c) => parseNestId(c.id));
  if (nestler.length) {
    const vurus = pointerWithin({ ...args, droppableContainers: nestler });
    if (vurus.length) return vurus;
  }
  return closestCenter({
    ...args,
    droppableContainers: kaplar.filter((c) => !parseNestId(c.id)),
  });
};

// YAPIŞKAN HEDEF: dnd-kit bırakma anında hedefi sıralamaya çevirebiliyor
// (sortable eklentisi kartları kaydırınca çarpışma yeniden hesaplanıyor).
// Bu yüzden "İÇİNE" hedefi dnd-kit'in KENDİ dragover olayından kaydedilir ve
// imleç 16 px'den fazla uzaklaşmadıkça geçerli kabul edilir. Kullanıcı noktadan
// bilinçli olarak ayrıldığında (16 px+) kayıt düşer → normal sıralama.
const YAPISKAN_PX = 16;
let yapiskan = null;

const konumOf = (event, manager) =>
  event?.operation?.position?.current || manager?.dragOperation?.position?.current || null;

export const nestHedefIzle = (event, manager) => {
  const hedef = parseNestId(event?.operation?.target?.id);
  const nokta = konumOf(event, manager);
  if (hedef) {
    yapiskan = { hedef, nokta };
    return hedef;
  }
  if (yapiskan && nokta && yapiskan.nokta) {
    const d = Math.hypot(nokta.x - yapiskan.nokta.x, nokta.y - yapiskan.nokta.y);
    if (d > YAPISKAN_PX) yapiskan = null;
  }
  return null;
};

export const nestHedefCoz = (event, manager) => {
  const dogrudan = parseNestId(event?.operation?.target?.id);
  if (dogrudan) return dogrudan;
  const nokta = konumOf(event, manager);
  if (!yapiskan) return null;
  if (nokta && yapiskan.nokta) {
    const d = Math.hypot(nokta.x - yapiskan.nokta.x, nokta.y - yapiskan.nokta.y);
    if (d > YAPISKAN_PX) return null;
  }
  return yapiskan.hedef;
};

export const nestHedefSifirla = () => { yapiskan = null; };

// "İÇİNE" noktası hedefken KENAR OTOMATİK KAYDIRMA durdurulur: aksi hâlde
// kullanıcı noktaya nişan alırken liste kaymaya devam eder, nokta imlecin
// altından kaçar ve bırakma sıralamaya düşer (gerçek hatanın kökü buydu).
export const otoKaydirmaKilidi = (manager, kilitli) => {
  const p = manager?.plugins?.get?.(AutoScroller);
  if (!p) return;
  if (kilitli) p.disable();
  else p.enable();
};

const DOT_SARMAL =
  "absolute right-0 top-1/2 z-20 grid h-11 w-11 -translate-y-1/2 place-items-center";
const DOT_ROZET =
  "pointer-events-none flex max-w-[66px] items-center gap-0.5 overflow-hidden rounded-full border px-1.5 py-0.5 font-mono text-[9px] font-semibold tracking-wider transition-all duration-150 border-sertex-cyan/60 bg-[#041018] text-sertex-cyan/90 nest-dot-badge";

// Görünüm küçük, DOKUNMA ALANI cömert (44x44) → parmakla ıskalanmaz.
// `gizli` (yalnızca hızlı mod): droppable kayıtlı kalır ama nokta görünmez,
// tıklanmaz ve `data-nest-id`/`data-testid` taşımaz (DOM'da "yok" sayılır).
const NestGorunum = ({ innerRef, anahtar, taskId, subId, aktifHedef, gizli = false }) => (
  <span
    ref={innerRef}
    {...(gizli
      ? { "aria-hidden": "true" }
      : {
          "data-nest-id": taskId,
          ...(subId ? { "data-nest-sub": subId } : {}),
          "data-nest-active": aktifHedef ? "true" : "false",
          "data-testid": `nest-drop-${anahtar}`,
          title: "Görevin içine taşı",
        })}
    className={gizli ? `${DOT_SARMAL} invisible pointer-events-none` : DOT_SARMAL}
  >
    <span className={DOT_ROZET}>
      <CornerDownRight className="h-3 w-3 shrink-0" />
      <span className="truncate nest-dot-text">İÇİNE</span>
    </span>
  </span>
);

// Hızlı mod noktası: HER ZAMAN kayıtlı (otomatik kaydırmada da bulunur).
// Gizliyken devre dışı + çarpışma algılayıcısı null döner → asla hedef olmaz.
// Görünürlük eskisi gibi `startTransition` ile bir kare sonra çizilir; böylece
// sürükleme başlangıç karesi noktaların ölçümüyle uzamaz.
const useNoktaGorunur = (depo, anahtar) => {
  const [gorunur, setGorunur] = useState(() => depo.gorunur(anahtar));
  useEffect(() => {
    const guncelle = () => {
      const v = depo.gorunur(anahtar);
      if (v) startTransition(() => setGorunur(true));
      else setGorunur(false);
    };
    guncelle();
    return depo.abone(guncelle);
  }, [depo, anahtar]);
  return gorunur;
};

const NestDotHizli = ({ depo, taskId, subId, anahtar }) => {
  const gorunur = useNoktaGorunur(depo, anahtar);
  const collisionDetector = useMemo(
    () => (input) => (depo.gorunur(anahtar) ? pointerIntersection(input) : null),
    [depo, anahtar],
  );
  const data = useMemo(() => ({ nest: true, taskId, subId }), [taskId, subId]);
  const { ref, isDropTarget } = useReactDroppable({
    id: nestDropId(taskId, subId),
    type: "nest",
    disabled: !gorunur,
    collisionDetector,
    collisionPriority: CollisionPriority.Highest,
    data,
  });
  return (
    <NestGorunum
      innerRef={ref} anahtar={anahtar} taskId={taskId} subId={subId}
      aktifHedef={gorunur && isDropTarget} gizli={!gorunur}
    />
  );
};

// --- @dnd-kit/react sürümü (kart listeleri + alt görevler) ---
const NestDotReact = ({ taskId, subId, anahtar }) => {
  const { ref, isDropTarget } = useReactDroppable({
    id: nestDropId(taskId, subId),
    type: "nest",
    collisionDetector: pointerIntersection,
    collisionPriority: CollisionPriority.Highest,
    data: { nest: true, taskId, subId },
  });
  return (
    <NestGorunum innerRef={ref} anahtar={anahtar} taskId={taskId} subId={subId} aktifHedef={isDropTarget} />
  );
};

// --- @dnd-kit/core sürümü (Kolay arayüzü) ---
const NestDotCore = ({ taskId, subId, anahtar }) => {
  const { setNodeRef, isOver } = useCoreDroppable({
    id: nestDropId(taskId, subId),
    data: { nest: true, taskId, subId },
  });
  return (
    <NestGorunum innerRef={setNodeRef} anahtar={anahtar} taskId={taskId} subId={subId} aktifHedef={isOver} />
  );
};

const gizliMi = (aktif, gecersiz, anahtar) =>
  !SURUKLE_ICINE_TASI_ACIK || !aktif || !!gecersiz?.has?.(anahtar);

export const NestDropDot = ({ taskId, subId = null }) => {
  const depo = useContext(NestDepoContext);
  const { aktif, gecersiz } = useContext(NestDropContext);
  const anahtar = subId || taskId;
  if (depo) {
    return SURUKLE_ICINE_TASI_ACIK
      ? <NestDotHizli depo={depo} taskId={taskId} subId={subId} anahtar={anahtar} />
      : null;
  }
  if (gizliMi(aktif, gecersiz, anahtar)) return null;
  return <NestDotReact taskId={taskId} subId={subId} anahtar={anahtar} />;
};

export const NestDropDotCore = ({ taskId, subId = null }) => {
  const { aktif, gecersiz } = useContext(NestDropContext);
  const anahtar = subId || taskId;
  if (gizliMi(aktif, gecersiz, anahtar)) return null;
  return <NestDotCore taskId={taskId} subId={subId} anahtar={anahtar} />;
};

// AŞAMA 12 — AYRILMIŞ (yüzen) PENCERELER için ortak "İÇİNE" sürükleme kancası.
// Ayrılmış pencereler kendi `DragDropProvider`'larını kullanıyor ve
// `NestDropContext.Provider` ile sarılmadıkları için nokta HİÇ çizilmiyordu
// (kullanıcının bildirdiği hata). Ana listedeki kurallar birebir aynıdır:
// nokta yalnızca sürükleme sırasında, yalnızca geçerli hedeflerde; hedef
// noktadayken sıralama önizlemesi uygulanmaz ve kenar kaydırma kilitlenir.
export const useNestDnd = ({ invalidDropIdsFor, dragCountOf, onDropIntoTask, kapsam = "pencere" }) => {
  const [nest, setNest] = useState(NEST_KAPALI);
  const aktifRef = useRef(false);
  // FAZ 3 — başka bir kapsamda (pencere/sidebar) sürükleme varsa burada da
  // noktalar çizilir; böylece pencereler arası bırakma hedefi görünür olur.
  // FAZ 4 — hızlı modda aynı kural depoda işletilir (React state'i yok).
  const depo = useNestDeposu(kapsam);
  const dis = useCrossNest(kapsam, !depo);

  const baslat = (event) => {
    if (!SURUKLE_ICINE_TASI_ACIK || !onDropIntoTask) return;
    const src = event?.operation?.source;
    nestHedefSifirla();
    const gecersiz = invalidDropIdsFor ? invalidDropIdsFor(src?.id, src?.data || {}) : null;
    aktifRef.current = true;
    if (depo) depo.yerelAyarla({ aktif: true, gecersiz });
    else startTransition(() => setNest({ aktif: true, gecersiz }));
    crossBaslat(kapsam, gecersiz);
    if (dragCountOf) {
      setDragCountBadge(
        dragCountOf(src?.id, src?.data || {}),
        src?.data?.kind === "subtask" ? "alt görev" : "görev",
      );
    }
  };

  // true dönerse çağıran taraf sıralama önizlemesini UYGULAMAZ.
  const uzerinde = (event, manager) => {
    const hedef = nestHedefIzle(event, manager);
    otoKaydirmaKilidi(manager, !!hedef);
    return !!hedef;
  };

  const bitir = (event, manager) => {
    let hedef = onDropIntoTask && !event?.canceled && aktifRef.current
      ? nestHedefCoz(event, manager)
      : null;
    // FAZ 3 — yerel hedef yoksa PENCERELER ARASI hedefe bakılır: imlecin
    // altındaki EN ÜST geçerli nokta kazanır. Nokta yoksa null döner ve
    // çağıran taraf bugünkü davranışını sürdürür (kalem kendi penceresine
    // çıkar) — "belirli hedef" her zaman "boş alan"ı ezer.
    if (!hedef && onDropIntoTask && !event?.canceled && aktifRef.current) {
      hedef = crossHedef();
    }
    otoKaydirmaKilidi(manager, false);
    aktifRef.current = false;
    crossBitir();
    if (depo) depo.yerelAyarla(NEST_KAPALI);
    else setNest(NEST_KAPALI);
    setDragCountBadge(0);
    return hedef;
  };

  return { nest: nest.aktif ? nest : dis, depo, baslat, uzerinde, bitir };
};

// Sürükleme sayaç göstergesi ("3 görev taşınıyor") — DOM'a doğrudan yazılır.
const BADGE_ID = "sx-drag-count-badge";
export const setDragCountBadge = (n, kind = "görev") => {
  let el = document.getElementById(BADGE_ID);
  if (!n || n < 2) { if (el) el.remove(); return; }
  if (!el) {
    el = document.createElement("div");
    el.id = BADGE_ID;
    el.setAttribute("data-testid", "drag-count-badge");
    el.style.cssText = [
      "position:fixed", "left:50%", "bottom:18px", "transform:translateX(-50%)",
      "z-index:3000", "pointer-events:none", "padding:6px 14px", "border-radius:9999px",
      "border:1px solid rgba(34,211,238,.6)", "background:rgba(4,12,18,.92)",
      "color:#67e8f9", "font:600 12px/1 ui-monospace,monospace", "letter-spacing:.08em",
    ].join(";");
    document.body.appendChild(el);
  }
  el.textContent = `${n} ${kind} taşınıyor`;
};
